<template>
  <div>
    <!-- Stripe Account Status -->
    <p v-if="stripeAccountIncomplete.incomplete"
      class="bg-red-300 p-6 rounded-lg shadow-lg border border-red-500 mb-6 flex items-baseline">
      Your Banking Setup is Incomplete.
      <button @click="openStripeConnect()" class="ml-2 btn-link link link-red mt-4">Click Here to Complete
        Setup</button>
    </p>

    <!-- Main Stats Grid -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-logo-alt2-light text-gray-700 p-6 rounded-lg shadow-lg border border-logo-alt2">
        <h3 class="text-sm font-medium">Ready for Payout</h3>
        <p class="text-2xl font-bold text-gray-800">${{ initialData.payoutBalance.toFixed(2) }}</p>
        <!-- <p class="text-xs text-gray-600">Paid on the Friday after the following week</p> -->
      </div>
      <div class="bg-logo-alt2-light text-gray-700 p-6 rounded-lg shadow-lg border border-logo-alt2">
        <h3 class="text-sm font-medium">Current Balance</h3>
        <p class="text-2xl font-bold text-gray-800">${{ Number(balance).toFixed(2) }}</p>
        <!-- <p class="text-sm">Current multiplier: {{ multiplier }}x</p> -->
      </div>
      <div class="bg-logo-alt2-light text-gray-700 p-6 rounded-lg shadow-lg border border-logo-alt2">
        <h3 class="text-gray-700 text-sm font-medium">Active Agencies (Multiplier)</h3>
        <p class="text-2xl font-bold text-gray-800">{{ activeAgencies }}</p>
      </div>
      <div class="bg-logo-alt2-light text-gray-700 p-6 rounded-lg shadow-lg border border-logo-alt2">
        <h3 class="text-gray-700 text-sm font-medium">Total Earnings (Paid to date)</h3>
        <p class="text-2xl font-bold text-gray-800">${{ totalEarnings.toFixed(2) }}</p>
      </div>
    </div>

    <!-- Affiliate Code Section -->
    <div class="bg-white p-6 rounded-lg shadow-lg border border-gray-100 mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-800">Affiliate Code</h2>
        <button v-if="!showCodeForm" @click="showCodeForm = true" class="text-logo hover:text-logo-dark text-sm">
          Change Code
        </button>
      </div>

      <div v-if="!showCodeForm" class="flex items-center space-x-3">
        <span class="font-mono text-lg text-logo font-bold">{{ affiliateCode }}</span>
        <button @click="copyCode" class="text-gray-400 hover:text-gray-600">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          </svg>
        </button>
      </div>

      <form v-else @submit.prevent="updateCode" class="space-y-4">
        <div>
          <div class="flex items-center mb-4">
            <input id="generate" v-model="codeOption" type="radio" value="generate" class="mr-2">
            <label for="generate" class="text-gray-700">Generate New Code (Random)</label>
          </div>
          <div class="flex items-center mb-4">
            <input id="custom" v-model="codeOption" type="radio" value="custom" class="mr-2">
            <label for="custom" class="text-gray-700">Set Custom Code</label>
          </div>
        </div>

        <div v-if="codeOption === 'custom'">
          <input v-model="customCode" type="text" placeholder="Enter your custom code"
            class="border rounded-lg p-2 w-full" :class="{ 'border-red-500': errors.custom_code }" minlength="10"
            required>
          <p v-if="errors.custom_code" class="text-red-500 text-xs mt-1 ml-2">{{ errors.custom_code[0] }}</p>
          <p class="text-gray-500 text-xs mt-1 ml-2">Must be at least 10 characters</p>
        </div>


        <div class="flex space-x-3">
          <button type="submit" class="btn btn-blue">
            Update Code
          </button>
          <button type="button" @click="showCodeForm = false" class="ml-4 link link-red">
            Cancel
          </button>
        </div>
      </form>
    </div>

    <!-- Weekly Earnings -->
    <div class="bg-white p-6 pt-4 rounded-lg shadow-lg border border-gray-100 mb-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">Weekly Earnings</h2>
      <table class="min-w-full divide-y divide-gray-200">
        <thead>
          <tr class="bg-logo-alt2-dark text-white border-b border-logo-alt2/20">
            <th class="px-4 py-2 text-left text-xs font-medium uppercase">Week Start</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase">Week End</th>
            <th class="px-4 py-2 text-center text-xs font-medium uppercase"># Assignments</th>
            <th class="px-4 py-2 text-center text-xs font-medium uppercase">Multiplier</th>
            <th class="px-4 py-2 text-right text-xs font-medium uppercase">Subtotal</th>
            <th class="px-4 py-2 text-right text-xs font-medium uppercase">Total</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <template v-for="(group, index) in initialData.weekly_data">
            <tr class="bg-logo-alt2-light border-b border-logo-alt2/20">
              <td class="px-4 py-2 text-sm font-semibold text-gray-900" colspan="5">
                {{ group.week_start }} to {{ group.week_end }}
              </td>
              <td class="px-4 py-2 text-sm font-semibold text-right text-gray-900">
                ${{ group.total.toFixed(2) }}
              </td>
            </tr>
            <tr 
              v-for="(row, rowIndex) in group.rows" 
              :key="`row-${group.week_start}-${row.multiplier}`" 
              :class="rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-logo-light/15'"
              class="hover:bg-gray-100 transition-colors duration-150"
            >
              <td class="px-4 py-2 text-sm text-gray-600">{{ row.week_start }}</td>
              <td class="px-4 py-2 text-sm text-gray-600">{{ row.week_end }}</td>
              <td class="px-4 py-2 text-sm text-center text-gray-900">{{ row.assignments_count }}</td>
              <td class="px-4 py-2 text-sm text-center text-gray-900">{{ row.multiplier }}x</td>
              <td class="px-4 py-2 text-sm text-right text-gray-700 italic"><span class="">$</span><span class="">{{ row.total.toFixed(2) }}</span></td>
              <td class="px-4 py-2 text-sm text-right text-gray-900"></td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>



    <div class="bg-white rounded-lg shadow-lg pt-4 p-6 mt-6">
      <div>
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-800">My Referrals</h2>
          <div @click="showActiveAgencies = !showActiveAgencies" class="h-6 w-6 cursor-pointer text-logo-alt2 hover:text-logo-alt2-dark">
            <div v-if="showActiveAgencies">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
              </svg>
            </div>
            <div v-else>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showActiveAgencies" class="mt-4">
        <table class="min-w-full divide-y divide-gray-100">
          <thead>
            <tr class="bg-logo-alt2-dark text-white border-b border-logo-alt2/20">
              <th class="px-4 py-2 text-left text-xs font-medium uppercase">Agency Name</th>
              <th class="px-4 py-2 text-right text-xs font-medium uppercase">Assignments (30 Days)</th>
              <th class="px-4 py-2 text-right text-xs font-medium uppercase">Count towards multiplier?</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(agency, i) in initialData.active_agencies_detail" :key="i" class="odd:bg-gray-50 even:bg-white hover:bg-gray-100 transition-colors duration-150">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ agency.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{{ agency.assignments_last_30_days }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-right">
                <span :class="agency.active ? 'text-green-600' : 'text-red-600'">
                  {{ agency.active ? 'Yes' : 'No' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>



    <!-- Stripe Account Management -->
    <div class="bg-white rounded-lg shadow-lg pt-4 p-6 mt-6">
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold text-gray-800">Banking Info:</h2>
        <div id="banking-management" @click="toggleBanking" class="cursor-pointer text-logo-alt2 hover:text-logo-alt2-dark h-6 w-6">
          <div v-if="showBanking">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </div>
          <div v-else>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </div>
      </div>
      <div v-show="showBanking" class="p-2 rounded-lg bg-white shadow-sm border border-gray-100 pt-4">
        <div class="flex justify-between items-center mb-4 px-4">
          <div class="flex justify-start space-x-4">
            <button v-for="tab in tabs" :key="tab" @click="activeTab = tab"
              :class="activeTab === tab ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 hover:bg-blue-300'"
              class="p-2 cursor-pointer text-center border-gray-200 capitalize rounded-lg">
              {{ tab }}
            </button>
          </div>
          <div>
            <a :href="stripeLoginLink" class="text-blue-600 hover:text-blue-800 text-sm cursor-pointer" target="_blank">
              <img src="/images/logos/Powered by Stripe - blurple.svg" alt="Stripe Logo" class="h-6">
            </a>
          </div>
        </div>
        <div v-show="activeTab === 'account'" class="bg-white p-6 mb-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">Payout Settings & Transactions</h3>
              <p class="text-gray-600 text-sm mt-1">Manage your payout methods and view transaction history</p>
            </div>
          </div>
          <div id="stripe-connect-container-account" ref="stripeConnect" class="min-h-[800px]"></div>
        </div>
        <div v-show="activeTab === 'payouts'" class="bg-white p-6 mb-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">Payouts</h3>
            </div>
          </div>
          <div id="stripe-connect-container-payouts" ref="stripeConnect" class="min-h-[800px]"></div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { loadConnectAndInitialize } from '@stripe/connect-js';

export default {
  props: {
    initialData: Object,
  },

  data() {
    return {
      showCodeForm: false,
      codeOption: 'generate',
      customCode: '',
      affiliateCode: this.initialData.affiliate_code,
      stripeAccountIncomplete: this.initialData.stripe_account_incomplete,
      stripeConnectUrl: this.initialData.stripe_connect_url,
      stripeLoginLink: this.initialData.stripe_login_link,
      balance: Number(this.initialData.balance || 0),
      multiplier: this.initialData.multiplier,
      activeAgencies: this.initialData.active_agencies,
      totalEarnings: Number(this.initialData.total_earnings || 0),
      recentTransactions: this.initialData.recent_transactions,
      errors: {},
      showBanking: false,
      bankingLoaded: false,
      stripeConnect: null,
      stripeAccountStarted: false,
      clientSecret: null,
      activeTab: 'account',
      showActiveAgencies: false,
      payoutBalance: Number(this.initialData.payoutBalance || 0),
    }
  },
  computed: {
    tabs() {
      return this.stripeAccountIncomplete.incomplete ? ['account'] : ['account', 'payouts'];
    },
  },

  mounted() {
    // this.initializeStripeConnect();
  },

  methods: {
    requestPayout(row) {
      console.log({ row });
    },

    toggleBanking() {
      if (!this.bankingLoaded) {
        this.initializeStripeConnect();
        this.bankingLoaded = true;
      }
      this.showBanking = !this.showBanking;
    },

    async updateCode() {
      try {
        const url = `/affiliates/${this.initialData.id}/updateCode`;
        console.log({ url });
        const response = await axios.patch(url, {
          code_option: this.codeOption,
          custom_code: this.customCode
        });

        this.affiliateCode = response.data.affiliate_code;
        this.showCodeForm = false;
        this.errors = {};

        flash('Affiliate code updated successfully');
      } catch (error) {
        if (error.response.status === 422) {
          this.errors = error.response.data.errors;
        }
      }
    },

    copyCode() {
      navigator.clipboard.writeText(this.affiliateCode);
      flash('Affiliate code copied to clipboard');
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    getStatusClass(transaction) {
      return transaction.paid_at
        ? 'px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
        : 'px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full';
    },

    async initializeStripeConnect() {
      try {
        const fetchClientSecret = async () => {
          const response = await axios.post(`/affiliates/${this.initialData.id}/create-connect-session`);
          if (response.data.error) {
            throw new Error(response.data.error);
          }
          this.clientSecret = response.data.clientSecret;
          console.log({ clientSecret: this.clientSecret });
          return response.data.clientSecret;
        };

        const stripeConnect = loadConnectAndInitialize({
          publishableKey: window.stripeKey,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0366d6',
              fontFamily: '"Nunito", system-ui, sans-serif',
              borderRadius: '8px',
              spacingUnit: '8px',
            },
          },
          fetchClientSecret: fetchClientSecret
        });

        // Create and mount the correct component
        if (this.stripeAccountIncomplete.incomplete) {
          const onboardingElement = stripeConnect.create('account-onboarding');
          onboardingElement.setCollectionOptions({
            fields: 'eventually_due',
            futureRequirements: 'include',
          });
          onboardingElement.setOnExit(() => {
            // console.log('Onboarding exited');
            // this.initializeStripeConnect();
            // reload the page
            location.reload();
          });
          const accountContainer = document.getElementById('stripe-connect-container-account');
          accountContainer.appendChild(onboardingElement);
        } else {
          const accountManagementElement = stripeConnect.create('account-management');
          const accountContainer = document.getElementById('stripe-connect-container-account');
          accountContainer.appendChild(accountManagementElement);
          const payoutsElement = stripeConnect.create('payouts');
          const payoutsContainer = document.getElementById('stripe-connect-container-payouts');
          payoutsContainer.appendChild(payoutsElement);
        }
      } catch (error) {
        console.error('Error initializing Stripe Connect:', error);
      }
    },

    async openStripeConnect() {
      this.showBanking = true;
      setTimeout(() => {
        this.scrollToBankingManagement();
      }, 200);
      if (!this.bankingLoaded) {
        this.initializeStripeConnect();
      }
    },

    scrollToBankingManagement() {
      const bankingManagement = document.getElementById('banking-management');
      bankingManagement.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
</script>